import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Clear from "@mui/icons-material/Clear";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Remove from "@mui/icons-material/Remove";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { SteersCredentialsPageDataQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import { useCallback, useMemo, useState } from "react";

import { AddCnDialog } from "./AddCnDialog";
import { AddCredentialAccessDialog } from "./AddCredentialAccessDialog";
import {
  ADD_STEERS_CREDENTIAL_ACCESS,
  GET_STEERS_CREDENTIALS_PAGE_DATA,
  REMOVE_STEERS_CREDENTIAL_ACCESS,
} from "./api";
import { EditCnDialog } from "./EditCnDialog";
import {
  parseSearchTerms,
  filterMappings,
  filterCredentials,
  filterCredentialsByAssociation,
} from "./utils";

const FULFILLMENT_URL = "/staff/fulfillment/facilities";

type Mapping = SteersCredentialsPageDataQuery["steersCredentialsPageData"]["mappings"][number];
type Credential =
  SteersCredentialsPageDataQuery["steersCredentialsPageData"]["credentials"][number];

type AccessDialogState = {
  action: "add" | "remove";
  credentialId: string;
  credentialUsername: string;
  alternateIdValue: string;
} | null;

export function SteersCredentials(): JSX.Element {
  const alerts = useAlerts();
  useBreadcrumb({ label: "STEERS Credentials" });

  const { data, loading, refetch } = useQuery(GET_STEERS_CREDENTIALS_PAGE_DATA);

  const {
    optimisticFilters: { search: cnSearch, credentials: credentialFilter },
    deferredFilters: {
      search: deferredCnSearch,
      credentials: deferredCredentialFilter,
      associatedCredentials,
    },
    setFilters,
  } = useSuspenseQueryParams<{
    search?: string;
    credentials?: string;
    associatedCredentials?: boolean;
  }>({ debounce: 250 });

  const [showAssociatedOnly, setShowAssociatedOnly] = useState(() => associatedCredentials);

  const setCnSearch = useCallback(
    (value: string) => {
      setFilters((state) => {
        state.search = value || undefined;
      });
    },
    [setFilters]
  );

  const setCredentialFilter = useCallback(
    (value: string) => {
      setFilters((state) => {
        state.credentials = value || undefined;
      });
    },
    [setFilters]
  );

  const toggleAssociatedOnly = useCallback(
    (checked: boolean) => {
      setFilters((state) => {
        state.associatedCredentials = checked || undefined;
      });
      setShowAssociatedOnly(checked);
    },
    [setFilters]
  );

  const [accessDialog, setAccessDialog] = useState<AccessDialogState>(null);
  const [showAddCnDialog, setShowAddCnDialog] = useState(false);
  const [showAddCredentialAccessDialog, setShowAddCredentialAccessDialog] = useState(false);
  const [editingCnValue, setEditingCnValue] = useState<string | null>(null);

  const [addAccess, { loading: addingAccess }] = useMutation(ADD_STEERS_CREDENTIAL_ACCESS, {
    onCompleted: () => {
      alerts.success("Credential access added successfully");
      setAccessDialog(null);
      refetch();
    },
    onError: (error) => {
      alerts.error("Failed to add credential access", error);
    },
  });

  const [removeAccess, { loading: removingAccess }] = useMutation(REMOVE_STEERS_CREDENTIAL_ACCESS, {
    onCompleted: () => {
      alerts.success("Credential access removed successfully");
      setAccessDialog(null);
      refetch();
    },
    onError: (error) => {
      alerts.error("Failed to remove credential access", error);
    },
  });

  const credentials = data?.steersCredentialsPageData.credentials;
  const mappings = useMemo(
    () => data?.steersCredentialsPageData.mappings ?? [],
    [data?.steersCredentialsPageData.mappings]
  );

  // Derive the editing mapping from fresh data
  const editingCn = useMemo(() => {
    if (!editingCnValue) return null;
    return mappings.find((m) => m.alternateIdInfo.alternateIdValue === editingCnValue) ?? null;
  }, [editingCnValue, mappings]);

  const handleAccessConfirm = useCallback(() => {
    if (!accessDialog) return;

    const variables = {
      credentialId: accessDialog.credentialId,
      alternateIdValue: accessDialog.alternateIdValue,
    };

    if (accessDialog.action === "add") {
      return addAccess({ variables });
    } else {
      return removeAccess({ variables });
    }
  }, [accessDialog, addAccess, removeAccess]);

  const handleCloseAddCnDialog = useCallback(() => {
    setShowAddCnDialog(false);
  }, []);

  const handleAddCnSuccess = useCallback(() => {
    setShowAddCnDialog(false);
    refetch();
  }, [refetch]);

  const handleCloseEditCnDialog = useCallback(() => {
    setEditingCnValue(null);
  }, []);

  const handleEditCnSuccess = useCallback(() => {
    setEditingCnValue(null);
    refetch();
  }, [refetch]);

  // Parse row search into terms (use deferred value for filtering)
  const rowSearchTerms = useMemo(() => parseSearchTerms(deferredCnSearch), [deferredCnSearch]);

  // Filter rows based on search terms (searches across all fields)
  // Computed before columns so we can use it for "Associated Credentials Only" filtering
  const filteredMappings = useMemo(
    () => filterMappings(mappings, rowSearchTerms),
    [mappings, rowSearchTerms]
  );

  const columns: GridColDef<Mapping>[] = useMemo(() => {
    const credentialList = credentials ?? [];
    const staticColumns: GridColDef<Mapping>[] = [
      {
        field: "alternateIdValue",
        headerName: "CN",
        width: 140,
        valueGetter: (_value, row) => row.alternateIdInfo.alternateIdValue,
      },
      {
        field: "tenant",
        headerName: "Tenant",
        minWidth: 150,
        maxWidth: 300,
        valueGetter: (_value, row) => row.alternateIdInfo.tenant?.name ?? "-",
        renderCell: (params) => {
          const tenantId = params.row.alternateIdInfo.tenant?.id;
          const tenantName = params.row.alternateIdInfo.tenant?.name;
          if (!tenantId || !tenantName) return "-";
          return (
            <Link
              href={`/o/${tenantId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {tenantName}
              <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 150,
        maxWidth: 300,
        valueGetter: (_value, row) => row.alternateIdInfo.description || "-",
      },
      {
        field: "credentialCount",
        headerName: "Credentials",
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "facilityCount",
        headerName: "Sites",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const cn = params.row.alternateIdInfo.alternateIdValue;
          const count = params.row.facilityCount;
          if (count === 0) return "0";
          return (
            <Link
              href={`${FULFILLMENT_URL}?omnisearch=${encodeURIComponent(`steersId:${cn}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {count}
            </Link>
          );
        },
      },
    ];

    // Filter credentials based on credentialFilter (separate from row search)
    // Use deferred value for filtering
    const credentialFilterTerms = parseSearchTerms(deferredCredentialFilter);
    let filteredCredentials = filterCredentials(credentialList, credentialFilterTerms);

    // When "Associated Credentials Only" is enabled, filter to only credentials
    // that have at least one association with the currently displayed rows
    if (showAssociatedOnly) {
      filteredCredentials = filterCredentialsByAssociation(filteredCredentials, filteredMappings);
    }

    const credentialColumns: GridColDef<Mapping>[] = filteredCredentials.map(
      (credential: Credential) => ({
        field: `credential_${credential.id}`,
        headerName: credential.username,
        width: 120,
        align: "center" as const,
        headerAlign: "center" as const,
        sortable: true,
        description: `${credential.name ?? credential.username} (${credential.cnCount} CNs)`,
        valueGetter: (_value, row) => {
          return row.associatedCredentialIds.includes(credential.id) ? 1 : 0;
        },
        renderHeader: () => {
          const credentialUrl = credential.tenantId
            ? `/o/${credential.tenantId}/credentials/${credential.id}`
            : null;
          const hasName = credential.name && credential.name !== credential.username;
          return (
            <Stack alignItems="center" sx={{ lineHeight: 1.2 }}>
              {credentialUrl ? (
                <Link
                  href={credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontWeight: 500 }}
                >
                  {credential.username}
                </Link>
              ) : (
                <span style={{ fontWeight: 500 }}>{credential.username}</span>
              )}
              {hasName && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {credential.name}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                {credential.cnCount} CNs
              </Typography>
            </Stack>
          );
        },
        renderCell: (params) => {
          const hasAccess = params.row.associatedCredentialIds.includes(credential.id);
          const dialogState = {
            credentialId: credential.id,
            credentialUsername: credential.username,
            alternateIdValue: params.row.alternateIdInfo.alternateIdValue,
          };

          if (hasAccess) {
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography color="success.main" fontWeight="medium">
                  Yes
                </Typography>
                <Tooltip title="Remove access">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccessDialog({ action: "remove", ...dialogState });
                    }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          }

          return (
            <Tooltip title="Add access">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setAccessDialog({ action: "add", ...dialogState });
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      })
    );

    return [...staticColumns, ...credentialColumns];
  }, [credentials, deferredCredentialFilter, filteredMappings, showAssociatedOnly]);

  const isAddingAccess = accessDialog?.action === "add";

  const getCnRowId = useCallback(
    (row: Mapping): string =>
      row.alternateIdInfo.id?.length
        ? row.alternateIdInfo.id
        : row.alternateIdInfo.alternateIdValue,
    []
  );

  return (
    <Box
      sx={{
        p: 3,
        height: "calc(100vh - 64px)", // Account for AppBar height
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexShrink: 0 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            label="Search"
            placeholder="CN123456 Acme Houston"
            value={cnSearch ?? ""}
            onChange={(e) => setCnSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: cnSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCnSearch("")} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            size="small"
            label="Filter credentials"
            placeholder="ER090712, ER080631"
            value={credentialFilter ?? ""}
            onChange={(e) => setCredentialFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            slotProps={{
              input: {
                endAdornment: credentialFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCredentialFilter("")} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showAssociatedOnly}
                onChange={(e) => toggleAssociatedOnly(e.target.checked)}
                size="small"
              />
            }
            label="Associated Credentials Only"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setShowAddCredentialAccessDialog(true)}
          >
            Add Credential Access
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddCnDialog(true)}>
            Add CN
          </Button>
        </Stack>
      </Stack>

      <DataGrid
        rows={filteredMappings}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter={false}
        autosizeOnMount
        autosizeOptions={{
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "alternateIdValue", sort: "asc" }],
          },
          pinnedColumns: {
            left: ["alternateIdValue"],
          },
        }}
        getRowId={getCnRowId}
        onRowClick={(params) => {
          setEditingCnValue(params.row.alternateIdInfo.alternateIdValue);
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />

      <ConfirmDialog
        open={!!accessDialog}
        header={isAddingAccess ? "Add Credential Access" : "Remove Credential Access"}
        onClose={() => setAccessDialog(null)}
        onConfirm={handleAccessConfirm}
        loading={isAddingAccess ? addingAccess : removingAccess}
        confirmText={isAddingAccess ? "Add Access" : "Remove Access"}
      >
        <Typography>
          {isAddingAccess ? "Grant" : "Remove"} credential{" "}
          <strong>{accessDialog?.credentialUsername}</strong>{" "}
          {isAddingAccess ? "access to" : "access from"} CN{" "}
          <strong>{accessDialog?.alternateIdValue}</strong>?
        </Typography>
      </ConfirmDialog>

      {showAddCnDialog && (
        <AddCnDialog onClose={handleCloseAddCnDialog} onSuccess={handleAddCnSuccess} />
      )}

      {editingCn && (
        <EditCnDialog
          mapping={editingCn}
          credentials={credentials ?? []}
          existingMappings={mappings}
          onClose={handleCloseEditCnDialog}
          onSuccess={handleEditCnSuccess}
          onRefresh={refetch}
        />
      )}

      {showAddCredentialAccessDialog && (
        <AddCredentialAccessDialog
          existingMappings={mappings}
          onClose={() => setShowAddCredentialAccessDialog(false)}
          onSuccess={() => {
            setShowAddCredentialAccessDialog(false);
            refetch();
          }}
        />
      )}
    </Box>
  );
}
